// @ts-nocheck

import { reduce } from 'lodash';
import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import {
  selectBasketAdditionalMessage,
  selectBasketBetslipId,
  selectBasketError,
  selectBasketIsCashout,
  selectBasketIsLoading,
  selectBasketMode,
  selectBasketStatus,
} from '../../selectors/basket/basketPrimary.selector';
import { selectBetshareBasketBetslipReference } from '../../selectors/basketCommon.selector';
import {
  selectBasketShowResume,
  selectBasketShouldShowMainBetConfirmation,
} from '../../selectors/basketCommon.selector';
import { selectBasketEnhancedItems, selectBasketNbItemInError } from '../../selectors/basketItems.selector';
import { BasketStatuses, BasketModes } from '../../types/basket';
import { createShallowEqualSelector } from '../../utils/reselect';

const getNbCashoutAddedItems = (items) => {
  return reduce(
    items,
    (prev, curr) => {
      if (curr.isCashout) return prev;
      return prev + 1;
    },
    0,
  );
};

const getTitle = (showResume, isCashout, nbCashoutAddedItems, nbItem) => {
  if (showResume) {
    return __('Bet confirmation');
  }
  if (isCashout) {
    if (nbCashoutAddedItems > 0) {
      return __('%1$s bet added', nbCashoutAddedItems, '%1$s bets added');
    }
    return null;
  }
  return __('%1$s bet selected', nbItem, '%1$s bets selected');
};

const getAlertContent = createSelector(
  [
    selectBasketShowResume,
    selectBasketNbItemInError,
    selectBasketMode,
    selectBasketStatus,
    selectBasketBetslipId,
    selectBasketAdditionalMessage,
    selectBasketError,
  ],
  (showResume, nbItemInError, mode, status, betslipId, additionalMessage, error) => {
    let content = null;
    let finalStatus = status;

    if (showResume) {
      if (mode !== BasketModes.SIMPLE) {
        const betType = (mode === BasketModes.COMBINED ? __('Accumulator') : __('System')).toLocaleLowerCase();

        switch (status) {
          case BasketStatuses.WAITING:
            content = __('Pending %1$s bets', betType);
            break;
          case BasketStatuses.REJECTED:
            content = __('Pending bet refused');
            break;
          default: {
            content = __('Bet validated!');
            const betslipIdParts = betslipId ? betslipId.split('-') : [];
            const finalBetslipId = betslipIdParts.length > 1 ? betslipIdParts[1] : betslipId;
            if (finalBetslipId !== undefined) {
              content += ` - ${finalBetslipId}`;
            }
            if (additionalMessage) {
              content += ` - ${additionalMessage}`;
            }
          }
        }
      }
    } else {
      if (mode === BasketModes.SIMPLE && nbItemInError > 0) {
        content = __('%1$s bet could not be validated', nbItemInError, '%1$s bets could not be validated');
        finalStatus = BasketStatuses.ERROR;
      } else if (error && error.estr) {
        content = error.estr;
      }
    }
    return { content, status: finalStatus };
  },
);

const getSimpleItems = createSelector([selectBasketEnhancedItems], (items) =>
  items.map((item) => ({ id: item.oddId, isCashout: item.cashout })),
);

const getIsBetshareOpen = (state, props) => props.location?.hash?.indexOf('share') !== -1;

const selectBasketProps = createShallowEqualSelector(
  [
    getSimpleItems,
    selectBasketShowResume,
    getAlertContent,
    selectBetshareBasketBetslipReference,
    selectBasketStatus,
    selectBasketIsLoading,
    selectBasketIsCashout,
    getIsBetshareOpen,
    selectBasketShouldShowMainBetConfirmation,
    selectBasketMode,
  ],
  (
    items,
    showResume,
    alert,
    betshareBetslipReference,
    basketStatus,
    isLoading,
    isCashout,
    isBetshareOpen,
    isShowingMainBetConfirmation,
    basketMode,
  ) => {
    const isValidating = basketStatus === BasketStatuses.PENDING;

    const nbItem = items.length;
    const isEmpty = nbItem === 0;
    const nbCashoutAddedItems = isCashout ? getNbCashoutAddedItems(items) : 0;
    const title = getTitle(showResume, isCashout, nbCashoutAddedItems, nbItem);
    const { content: alertContent, status: alertStatus } = alert;

    return {
      isLoading,
      isValidating,
      isEmpty,
      items,
      isCashout,
      title,
      alertContent,
      alertStatus,
      betshareBetslipReference,
      isBetshareOpen,
      isShowingMainBetConfirmation,
      basketMode,
    };
  },
);

export default selectBasketProps;
